import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Row, Spinner } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { T, useTranslate, useSetLanguage, useCurrentLanguage } from "@tolgee/react";
interface Payment {
  currency: string
  amount: number
  message: string
  contact: Contact | null
}
interface Contact {
  firstname: string
  lastname: string
  email: string
  address: string
  postcode: string
  city: string
  province: string
  country: string
}
interface Currency {
  name: string
  code: string
  maximumFractionDigits?: number
}
interface ClientPaymentForm {
  name: string;
  min: number | null;
  max: number | null;
  return_url: string | null;
  currencies: Currency[];
  defaultCurrency: string;
  embed: boolean
}
interface Country {
  name: string
  code: string
}
interface Result {
  status: string
  reference: string
  amount: number
  currency: string
  client: string
  test: true
  owner: string | null
  contact_email: string
  contact: string
  iat: number
  nbf: number
  exp: number
  iss: string
  jti: string
  lang?: string
}


let resultVariants: Record<string, string | undefined> = {
  "failed": 'danger',
  "success": 'success',
  "cancelled": 'warning',
  "started": 'info'
}

function Asterisk() {
  return <b className="text-danger">*</b>
}
function App() {
  let [form, setForm] = useState<ClientPaymentForm | null>(null);
  let [countries, setCountries] = useState<Country[]>([]);
  let t = useTranslate();
  let [error, setError] = useState<number>(0);
  let [loading, setLoading] = useState(false);
  let [result, setResult] = useState<Result | null>(null);
  let setLanguage = useSetLanguage();
  let [lang, setLang] = useState('en');
  useEffect(() => {
    let params = new URLSearchParams(window.location.search);
    if (params.has('lang')) {
      setLanguage(params.get('lang')!)
      setLang(params.get('lang')!)
    } else {
      let lang = window.navigator.language?.split('-')[0];
      if (lang) {
        setLanguage(lang);
        setLang(lang);
      }
    }
  }, [])
  let [token, setToken] = useState(() => {
    let params = new URLSearchParams(window.location.search);
    let token = params.get('return_token');
    if (token) {
      return token;
    } else {
      return null
    }
  })
  let uForm = useForm<Payment>({
  });
  let location = useMemo(() => {
    return window.location.pathname.split('/').filter(a => a)
  }, [])
  let formName = useMemo(() => {
    return location.slice(-1);
  }, [location])
  let load = useCallback(() => {
    setError(0);
    setResult(null);
    if (location.length !== 2 || location[0] !== 'payment' || !formName) {
      setForm(null);
      return;
    }
    setLoading(true);
    uForm.resetField('amount')
    Promise.all([countries.length === 0 && fetch(`/payment/api/countries`, {
      headers: {
        accept: 'application/json',
        'accept-language': lang
      }
    })
      .then(r => {
        if (r.ok) {
          r.json().then((cs: Country[]) => {
            setCountries(cs);
          })
        } else {
          setForm(null);
          setError(r.status);
        }
      }),
    fetch(`/payment/api/form/${formName}`, {
      headers: {
        accept: 'application/json',
        'accept-language': lang
      }
    })
      .then(r => {
        if (r.ok) {
          r.json().then((f: ClientPaymentForm) => {
            setForm(f);
            setError(0);
            uForm.reset({
              currency: f.defaultCurrency,
              amount: undefined
            })
          })
        } else {
          setForm(null);
          setError(r.status);
        }
      }),
    token && fetch(`/payment/api/form/${formName}/token?token=${token}`, {
      headers: {
        'accept-language': lang
      }
    })
      .then(r => {
        setToken(null);
        window.history.replaceState(null, '', window.location.pathname.split('?')[0])
        if (r.ok) {
          r.json().then((v: Result) => {
            setResult(v);
            if (v.lang) {
              setLanguage(v.lang);
              setLang(v.lang);
            }
          })
        } else {
          setError(703)
        }
      })
    ]).finally(() => {
      setLoading(false);
    });
  }, [countries.length, formName, lang, location, token, uForm]);
  useEffect(function () {
    load();
  }, [load])
  let errMsg = error !== 0 ? t(`err_${error}`, `Error ${error}`) : null;


  let onSubmit = useCallback<SubmitHandler<Payment>>((data) => {
    setLoading(true);
    fetch(`/payment/api/form/${formName}`, {
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'accept-language': lang
      },
      method: 'POST',
      body: JSON.stringify(data)
    })
      .then(r => {
        return r.json().then(v => {
          if (r.ok) {
            setError(0);
            if (v.render) {
              document.open();
              document.write(v.render);
              document.close();
            }
          } else {
            setError(v.code ?? 701)
          }
        }).catch(e => setError(701))
      }).finally(() => {
        setLoading(false)
      });
  }, [formName, lang]);

  useEffect(() => {
    if (form)
      document.title = `${t(`form_${form.name}_name`)} - ${t(`form_${form?.name}_title`)}` 
  }, [form, t])
  if (loading) {
    return <Spinner className="m-5 p-5 text-info" animation="border"></Spinner>
  } else if (errMsg) {
    return <Alert className="m-5" variant="danger">
      <p>{errMsg}</p>
      <Button onClick={load}>
        <T keyName="result_restart_payment_button" />
      </Button>
    </Alert>
  } else if (result) {
    return <Alert className="m-5" variant={resultVariants[result.status]}>
      <p>{t(`payment_result_${ result.status }`, result.status)}</p>
      {result.test && <p><T keyName="warning_test_system" parameters={{ b: <b /> }} /></p>}
      <Button onClick={load}>
        <T keyName="result_restart_payment_button" />
      </Button>
    </Alert>
  } else if (form) {
    return (
      <div className={`App ${ form.embed ? 'p-1' : 'p-4' } `}>
        {!form.embed &&
          <div>
            <h2>
              <T keyName={`form_${ form?.name }_name`} />
              &nbsp;
              -
              &nbsp;
              <T keyName={`form_${ form?.name }_title`} />
            </h2>
            <p><T keyName={`form_${ form?.name }_intro`}>&nbsp;</T></p>
          </div>
        }
        <Form className="container-fluid" onSubmit={uForm.handleSubmit(onSubmit)}>
          <Form.Group className="mb-3" controlId="currency">
            <Form.Label><T keyName="payment_form_currency_label" /> <Asterisk /></Form.Label>
            <Form.Select {...uForm.register('currency', {
              required: true
            })}>
              {form.currencies.map(c =>
                <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
              )}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3" controlId="amount">
            <Form.Label><T keyName="payment_form_amount_label" /> <Asterisk /></Form.Label>
            <Form.Control
              type="number"
              min={form.min ?? 0}
              max={form.max ?? undefined}
              step="any"
              {...uForm.register('amount', {
                required: true,
                valueAsNumber: true
              })}
            />

          </Form.Group>
          <Form.Group className="mb-3" controlId="message">
            <Form.Label><T keyName="payment_form_message_label" /></Form.Label>
            <Form.Control {...uForm.register('message')} as="textarea" rows={4} maxLength={500} />
          </Form.Group>
          <h3><T keyName="payment_form_contact_title" /></h3>
          <Row>

            <Form.Group className="mb-3 col-md-6" controlId="firstname">
              <Form.Label><T keyName="payment_form_contact_firstname_label" /> <Asterisk /></Form.Label>
              <Form.Control maxLength={128}  {...uForm.register('contact.firstname', {
                required: true
              })} />
            </Form.Group>
            <Form.Group className="mb-3 col-md-6" controlId="lastname">
              <Form.Label><T keyName="payment_form_contact_lastname_label" /> <Asterisk /></Form.Label>
              <Form.Control maxLength={128}  {...uForm.register('contact.lastname', {
                required: true
              })} />
            </Form.Group>
            <Form.Group className="mb-3 col-md-12" controlId="email">
              <Form.Label><T keyName="payment_form_contact_email_label" /> <Asterisk /></Form.Label>
              <Form.Control {...uForm.register('contact.email', {
                required: true
              })} maxLength={256} type="email" />
            </Form.Group>
            <Form.Group className="mb-3 col-md-12" controlId="address">
              <Form.Label><T keyName="payment_form_address_label" /></Form.Label>
              <Form.Control maxLength={256} as="textarea" rows={2} {...uForm.register('contact.address')} />
            </Form.Group>
            <Form.Group className="mb-3 col-md-4" controlId="postcode">
              <Form.Label><T keyName="payment_form_postcode_label" /></Form.Label>
              <Form.Control maxLength={48} {...uForm.register('contact.postcode')} />
            </Form.Group>
            <Form.Group className="mb-3 col-md-8" controlId="city">
              <Form.Label><T keyName="payment_form_city_label" /></Form.Label>
              <Form.Control maxLength={128} {...uForm.register('contact.city')} />
            </Form.Group>
            <Form.Group className="mb-3 col-md-7" controlId="country">
              <Form.Label><T keyName="payment_form_country_label" /></Form.Label>
              <Form.Select maxLength={128}  {...uForm.register('contact.country')}>
                <option></option>
                {countries.map(c =>
                  <option key={c.code} value={c.code}>{c.name}</option>)}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3 col-md-5" controlId="province">
              <Form.Label><T keyName="payment_form_province_label" /></Form.Label>
              <Form.Control maxLength={128}  {...uForm.register('contact.province')} />
            </Form.Group>
          </Row>
          <Button variant="primary" type="submit">
            <T keyName="payment_form_submit_button" />
          </Button>
        </Form>
      </div>
    );
  } else {
    return <></>
  }
}

export default App;
